import { Card } from "@2u-landing-pages/components/Card";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { ItemsContainer } from "@2u-landing-pages/components/ItemsContainer";
import { ProductCard } from "@2u-landing-pages/components/ProductCard";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { VideoEmbed } from "@2u-landing-pages/components/VideoEmbed";
import {
  ContentfulImageConfig,
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeCardWithoutUnresolvableLinksResponse,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeDetail_calloutWithoutUnresolvableLinksResponse,
  TypeItems_containerWithoutUnresolvableLinksResponse,
  TypeMarqueeWithoutUnresolvableLinksResponse,
  TypeProduct_cardWithoutUnresolvableLinksResponse,
  TypeVideoWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useEqualHeights } from "@2u-landing-pages/utils";
import { useEqualHeightsOnHighlightChange } from "@2u-landing-pages/providers";
import { Split as BricksSplit } from "@red-digital/bricks";
import cn from "classnames";
import React, { useEffect, useMemo } from "react";
import { Bento } from "../Bento";
import { DetailCallout } from "../DetailCallout";
import { Marquee } from "../Marquee";
import { BricksSplitProps, CustomCSSProperties, SplitProps } from "./Split.types";
// @ts-ignore
import styles from "./Split.module.scss";

export const Split: React.FC<SplitProps> = ({
  orientation,
  split_configuration = "50/50",
  vertical_gap = "Medium (48/24)",
  slots,
  section_background_color,
  section_background_image,
  section_top_padding,
  section_bottom_padding,
  secondary_background_color,
  phoneNumber,
  alignment = orientation === "horizontal" ? "left-center" : "center-center",
  split_order_on_mobile = false,
  image_config,
  inspector,
  mode,
}) => {
  const style: CustomCSSProperties = {};
  const splitProps: BricksSplitProps = {
    direction: split_order_on_mobile ? "column-reverse" : "column",
  };

  const isVertical = orientation === "vertical";

  // Check if both slots contain Product Cards
  const productCardSlots = useMemo(() => {
    return slots?.filter((slot) => slot?.sys?.contentType?.sys?.id === "product_card") || [];
  }, [slots]);

  const shouldUseEqualHeights = productCardSlots.length === 2;
  const { registerElement, updateHeights } = useEqualHeights({ isSplitMode: shouldUseEqualHeights });

  useEqualHeightsOnHighlightChange(updateHeights, shouldUseEqualHeights);

  // Trigger height update when slots change or component mounts
  useEffect(() => {
    if (shouldUseEqualHeights) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        updateHeights();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [shouldUseEqualHeights, updateHeights, slots]);

  if (orientation === "horizontal") {
    splitProps.lg = {
      direction: "row",
    };
  }

  if (secondary_background_color) style["--secondary-background-color"] = `var(${secondary_background_color})`;
  if (alignment) {
    const alignmentSuffix = alignment.split("-").pop();
    const buttonAlignmentMap: Record<string, string> = {
      left: "flex-start",
      right: "flex-end",
      center: "center",
      "left-center": "flex-start",
      "right-center": "flex-end",
    };

    style["--button-alignment"] =
      buttonAlignmentMap[alignment] || (alignmentSuffix && buttonAlignmentMap[alignmentSuffix]) || alignment;
  }

  return (
    <TemplateWrapper
      padded
      backgroundColor={section_background_color}
      backgroundImage={section_background_image}
      image_config={image_config as unknown as ContentfulImageConfig}
      paddingTop={section_top_padding}
      paddingBottom={section_bottom_padding}
      style={style}
    >
      <BricksSplit
        {...splitProps}
        className={cn(styles.split, {
          [styles.vertical]: orientation === "vertical",
          [styles.horizontal]: orientation === "horizontal",
          [styles.split_50_50]: split_configuration === "50/50",
          [styles.split_70_30]: split_configuration === "70/30",
          [styles.split_30_70]: split_configuration === "30/70",
          [styles.vertical_gap_large]: vertical_gap === "Large (64/32)",
          [styles.vertical_gap_medium]: vertical_gap === "Medium (48/24)",
          [styles.vertical_gap_small]: vertical_gap === "Small (32/16)",
          [styles.vertical_gap_xsmall]: vertical_gap === "X-Small (24/12)",
        })}
      >
        {slots?.map((slot, index) => {
          const classNames = cn(styles.slot, styles[`${slot?.sys?.contentType?.sys?.id}-${orientation}`], {
            [styles.slotFull]: slots?.length === 1,
          });

          switch (slot?.sys?.contentType?.sys?.id) {
            case "items_container":
              return (
                <ItemsContainer
                  key={slot?.sys?.id}
                  orientation={orientation}
                  className={classNames}
                  entry={slot as TypeItems_containerWithoutUnresolvableLinksResponse}
                  phoneNumber={phoneNumber}
                />
              );
            case "content_full": {
              const { image, eyebrow, heading, subhead, body, image_aspect_ratio } = (
                slot as TypeContent_fullWithoutUnresolvableLinksResponse
              ).fields;
              const isImageOnly = Boolean(image && !eyebrow && !heading && !subhead && !body);
              const isSvgImage = image?.fields?.file?.contentType === "image/svg+xml";

              return (
                <div
                  key={slot?.sys?.id}
                  className={cn(classNames, {
                    [styles.secondaryBackground]: Boolean(secondary_background_color),
                  })}
                >
                  <div
                    className={cn({
                      [styles.alignmentContainer]: Boolean(alignment) && !isVertical,
                      [styles.contentfulImageWrapper]: isImageOnly,
                      [styles.svgImage]: isImageOnly && isSvgImage,
                    })}
                  >
                    <ContentLayout
                      className={cn(styles.contentLayout, {
                        [styles[alignment]]: Boolean(alignment),
                      })}
                      content={slot as TypeContent_fullWithoutUnresolvableLinksResponse}
                      image_aspect_ratio={image_aspect_ratio}
                      phoneNumber={phoneNumber}
                    />
                  </div>
                </div>
              );
            }
            case "bento":
              return (
                <Bento
                  key={slot?.sys?.id}
                  className={classNames}
                  embedded
                  {...(slot as TypeBentoWithoutUnresolvableLinksResponse)?.fields}
                  inspector={inspector}
                  phoneNumber={phoneNumber}
                />
              );
            case "detail_callout":
              return (
                <DetailCallout
                  key={slot?.sys?.id}
                  className={classNames}
                  phoneNumber={phoneNumber}
                  {...(slot as TypeDetail_calloutWithoutUnresolvableLinksResponse)?.fields}
                />
              );
            case "card":
              return (
                <Card
                  key={slot?.sys?.id}
                  className={classNames}
                  entry={slot as TypeCardWithoutUnresolvableLinksResponse}
                  phoneNumber={phoneNumber}
                />
              );
            case "marquee":
              return (
                <Marquee
                  id={slot?.sys?.id}
                  key={slot?.sys?.id}
                  {...(slot as TypeMarqueeWithoutUnresolvableLinksResponse).fields}
                />
              );
            case "product_card":
              const program = slot as TypeProduct_cardWithoutUnresolvableLinksResponse;
              const programId = program.sys.id;
              return (
                <ProductCard
                  className={styles.slot}
                  key={programId}
                  ref={(el) => {
                    if (el) {
                      el.dataset.location = "Split";
                      el.dataset.position = index.toString();
                      // Register element for equal heights if both sides have Product Cards
                      if (shouldUseEqualHeights) {
                        registerElement(programId, el);
                      }
                    }
                  }}
                  product={program}
                  position={index + 1}
                  mode={mode}
                  standalone={true}
                />
              );
            case "video":
              return (
                <div key={slot?.sys?.id} className={classNames}>
                  <VideoEmbed {...(slot as TypeVideoWithoutUnresolvableLinksResponse)?.fields} />
                </div>
              );
            default:
              return null;
          }
        })}
      </BricksSplit>
    </TemplateWrapper>
  );
};
